import { apiMutate } from "../hooks/useApi";
import type { OfferOption, OfferOptionDay } from "./types";
import { Button, Drawer, Icon, Input, ZeitEingabe, DateInput } from "./components";
import { AdresseFelder, type AdresseFeld } from "./AdresseFelder";
import { LAENDER } from "../lib/laender";
import { adresseEinzeilig } from "../lib/adresse-anzeige";

/** Muss mit `OHNE_TAG` in `builder.tsx` uebereinstimmen (Schema laesst nur `dayNo >= 0` zu, s.
 *  dortiger Kommentar). Kein neuer Wert — derselbe Sentinel, nur an zwei Stellen literal, weil
 *  `builder.tsx` ihn nicht exportiert (er verlaesst die Oberflaeche laut Konzept nie). */
const OHNE_TAG = -1;

/** Bildet ein `AdresseFeld` (`ui/AdresseFelder.tsx`) auf das PascalCase-Suffix ab, das
 *  `PATCH /api/events/:id` erwartet (z. B. "street" -> "Street", zusammengesetzt zu
 *  "eventAddressStreet"/"deliveryAddressStreet") — dasselbe Namensschema wie die bereits
 *  bestehenden `eventAddress*`/`deliveryAddress*`-Felder in `lib/event-schema.ts`. */
function feldSuffix(feld: AdresseFeld): string {
  const suffix: Record<AdresseFeld, string> = {
    company: "Company",
    name: "Name",
    street: "Street",
    addition: "Addition",
    zip: "Zip",
    city: "City",
    countryCode: "CountryCode",
  };
  return suffix[feld];
}

/**
 * Rueckbau der Inline-Eckdaten (01.09.2026, Antoine nach dem Ausprobieren: „...es macht doch mehr
 * Sinn, wenn es nicht inline geändert wird, sondern wenn sich das Fenster öffnet und ich dort
 * alles ändern kann, nachdem ich auf Personenanzahl oder Datum oder ähnliches geklickt habe.")
 *
 * ── Die kurze Geschichte dieser Datei ─────────────────────────────────────────────────────────
 * Bis A3 (24.08.2026) gab es dafuer den Nachschlage-Reiter „Variante bearbeiten". A3 loeste ihn
 * ab durch GENAU DIESEN Drawer (`VeranstaltungsDatenDrawer`, geoeffnet ueber EINEN Klick auf die
 * Meta-Zeile im Seitenkopf). Ab 01.09.2026 (erste Fassung) wurde daraus kurzzeitig
 * `TagesplanungPanel` — Datum/Gaestezahl der Veranstaltung wurden eigene, einzeln editierbare
 * Inline-Chips direkt in der Meta-Zeile, nur die Gaeste-VARIANTE + Tagesplanung blieben als
 * inline ausklappbarer Bereich zurueck. Nach dem Ausprobieren wollte Antoine dieses Verhalten
 * NICHT: er tippte staendig auf einzelne Chips, obwohl er „alles auf einmal" aendern wollte.
 * Dieser Umbau (spaeter, ebenfalls 01.09.2026) macht den Drawer wieder zu EINEM Fenster fuer
 * ALLES — Datum, Gaestezahl der Veranstaltung, Uhrzeit, Adresse dieses Auftrags, Gaeste-VARIANTE
 * und Tagesplanung —, geoeffnet ueber EINEN Klick auf die ganze Meta-Zeile
 * (`builder-eckdaten-oeffnen` in `builder.tsx`).
 *
 * ── Was seit A3 NEU dazugekommen ist und jetzt mit ins Fenster wandert ───────────────────────
 * Zwei echte Verbesserungen sind seit dem urspruenglichen A3-Drawer entstanden und bleiben
 * erhalten — nur ihr UI-Container wechselt zurueck ins Fenster:
 *   1. Uhrzeit (`events.timeFrom`/`timeTo`, UX-Paket 26.08.2026): der A3-Drawer zeigte sie noch
 *      gar nicht. Steht jetzt als eigenes Feldpaar hier drin (s. `patchEventTime` unten).
 *   2. „Adresse dieses Auftrags" (Migration 350, `docs/KONZEPT-AUFTRAGSADRESSE.md` § 5, PR #829):
 *      war zwischenzeitlich ein eigener, vom Tagesplanung-Bereich unabhaengiger ausklappbarer
 *      Bereich unter der Meta-Zeile (weil sie laut Konzept „immer verfuegbar" sein soll, auch an
 *      einem bereits gebundenen Auftrag — `CONTRACT_LOCKED_FIELDS` in `routes/events.ts` schliesst
 *      sie bewusst aus). Dieselbe Begruendung gilt weiter, nur der Container ist jetzt dieses
 *      Fenster statt ein eigener Klapp-Bereich — deshalb steht sie hier UNCONDITIONAL, nicht unter
 *      dem `editable && option`-Gate der Variante (s. unten).
 *
 * ── Zwei Gaestezahlen, und warum beide hier stehen ───────────────────────────────────────────
 * `event.guestCount` ist die Gaestezahl der VERANSTALTUNG (sie steht in der Kopfzeile und in
 * jeder Uebersicht). `option.guestCount` ist die der VARIANTE — und nur DIESE geht in die
 * Preisrechnung ein (`packages/pricing`, `optionGuestCount`). Sie koennen bewusst auseinander
 * liegen („Angebot fuer 80, Variante fuer 100 durchgerechnet"). Beide sind hier sichtbar und
 * einzeln aenderbar, mit dem Hinweis, welche rechnet.
 *
 * ── Zustands-Grenze (unveraendert seit A3) ───────────────────────────────────────────────────
 * `days`/`activeDay` bleiben fachlicher Zustand des Parents (Konzept § 3): `activeDay` speist
 * ausserhalb dieses Drawers `neueZeilenTag` und den Sichtbarkeits-Filter der Positionsliste.
 * `enableDays`/`addDay`/`patchDay`/`removeDay` bleiben ebenfalls beim Parent (eigene H-12-Gates)
 * und kommen als Callback-Props herein. Die PATCHes fuer Datum/Gaestezahl/Uhrzeit/Adresse laufen
 * als Inline-`onBlur`-Aufrufe ueber `apiMutate`, lokal in dieser Komponente gebaut (weniger Props
 * als sie durch `builder.tsx` durchzureichen — Praezedenzfall: derselbe Aufbau im A3-Drawer).
 */
export function VeranstaltungsDatenDrawer({
  open,
  onClose,
  eventId,
  eventDate,
  eventGuestCount,
  eventTimeFrom,
  eventTimeTo,
  eventAddressStreet,
  eventAddressZip,
  eventAddressCity,
  eventAddressCompany,
  eventAddressName,
  eventAddressAddition,
  eventAddressCountryCode,
  deliveryAddressStreet,
  deliveryAddressZip,
  deliveryAddressCity,
  deliveryAddressCompany,
  deliveryAddressName,
  deliveryAddressAddition,
  deliveryAddressCountryCode,
  customerAddressStreet,
  customerAddressZip,
  customerAddressCity,
  customerCompany,
  customerName,
  customerAddressAddition,
  customerAddressCountryCode,
  onEventChanged,
  option,
  activeId,
  onOptionChange,
  save,
  setError,
  editable,
  days,
  activeDay,
  onSelectDay,
  zeigeOhneTagReiter,
  ohneTagAnzahl,
  onEnableDays,
  onAddDay,
  onPatchDay,
  onRequestDeleteDay,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  eventDate: string | null | undefined;
  eventGuestCount: number | null | undefined;
  eventTimeFrom: string | null | undefined;
  eventTimeTo: string | null | undefined;
  eventAddressStreet: string | null | undefined;
  eventAddressZip: string | null | undefined;
  eventAddressCity: string | null | undefined;
  /** Migration 422 (Adressmodell, WARTESCHLANGE Punkt 7): Firma/Name bleiben NUR
   *  Anzeige-/Lieferbeschriftung — sie ueberschreiben NIE den Rechnungsempfaenger-Namen (§ 3.2
   *  Konzept-Dokument, von Antoine "so lassen" bestaetigt). */
  eventAddressCompany: string | null | undefined;
  eventAddressName: string | null | undefined;
  eventAddressAddition: string | null | undefined;
  eventAddressCountryCode: string | null | undefined;
  /** Migration 385 (WP3b, Auftrags-Dashboard „Phase C.1"): eigenstaendige Lieferadresse, GETRENNT
   *  von der Rechnungsadresse oben (`eventAddress*`) — s. Kommentar am Abschnitt unten. */
  deliveryAddressStreet: string | null | undefined;
  deliveryAddressZip: string | null | undefined;
  deliveryAddressCity: string | null | undefined;
  deliveryAddressCompany: string | null | undefined;
  deliveryAddressName: string | null | undefined;
  deliveryAddressAddition: string | null | undefined;
  deliveryAddressCountryCode: string | null | undefined;
  customerAddressStreet: string | null | undefined;
  customerAddressZip: string | null | undefined;
  customerAddressCity: string | null | undefined;
  customerCompany: string | null | undefined;
  customerName: string | null | undefined;
  customerAddressAddition: string | null | undefined;
  customerAddressCountryCode: string | null | undefined;
  onEventChanged: () => void;
  option: OfferOption | null;
  activeId: string | null;
  onOptionChange: (option: OfferOption) => void;
  save: () => Promise<unknown>;
  setError: (message: string) => void;
  editable: boolean;
  days: OfferOptionDay[];
  activeDay: number | null;
  onSelectDay: (dayNo: number) => void;
  zeigeOhneTagReiter: boolean;
  ohneTagAnzahl: number;
  onEnableDays: () => void;
  onAddDay: () => void;
  onPatchDay: (dayNo: number, patch: Partial<OfferOptionDay>) => void;
  onRequestDeleteDay: (dayNo: number) => void;
}) {
  /** Veranstaltungsdatum. Leeres Feld = Datum entfernen ist NICHT vorgesehen (die Route nimmt
   *  nur ein gueltiges Datum entgegen) — ein leeres Feld bleibt darum folgenlos, statt einen
   *  422 auszuloesen, den niemand verursachen wollte. */
  async function patchEventDate(value: string) {
    if (!eventId || !value) return;
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { eventDate: value });
      onEventChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function patchEventGuestCount(value: number) {
    if (!eventId) return;
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { guestCount: value });
      onEventChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Uhrzeit (`events.timeFrom`/`timeTo`) — seit dem UX-Paket 26.08.2026 Teil des Modells, mit
   *  diesem Ruecksbau (01.09.2026) NEU MIT INS FENSTER genommen (der A3-Drawer zeigte sie noch
   *  gar nicht). Absichtlich OHNE eigenen Speichern/Abbrechen-Knopf und ohne lokalen Busy-/
   *  Fehler-Zustand (anders als die kurzlebige Inline-Fassung `zeitSpeichern` in `builder.tsx`,
   *  die dieser Umbau ersetzt) — das Fenster IST bereits der Bearbeiten-Modus, ein zweiter
   *  „Speichern"-Knopf darin waere doppelt gemoppelt. Der PATCH geht raus, sobald der Fokus den
   *  GANZEN Block verlaesst (nicht schon beim Wechsel von „Von" zu „Bis" innerhalb des Blocks —
   *  Pruefung ueber `relatedTarget`, dasselbe Prinzip wie `useCloseGuard` in `ui/components.tsx`),
   *  und liest die aktuellen Werte direkt aus dem DOM statt sie zusaetzlich im State
   *  mitzufuehren (wie `patchEventDate`/`patchEventGuestCount` oben: `e.target.value`, kein
   *  kontrollierter Zwischenstand). Leeres „Von" bleibt folgenlos — wortgleiche Regel wie zuvor. */
  async function patchEventTime(from: string, to: string) {
    if (!eventId || !from) return;
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { timeFrom: from, timeTo: to || null });
      onEventChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Migration 350 (Auftragsadresse, docs/KONZEPT-AUFTRAGSADRESSE.md § 5). Gesetzt (alle drei
   *  Felder), hat sie Vorrang vor der Kundenadresse fuer Wetter/Ort UND Rechnung; leer, gilt
   *  weiterhin die Kundenadresse. Ein Feld je Aufruf, PATCH ist partiell — leerer Wert wird als
   *  `null` gesendet (loeschbar). WP3b (Migration 385): derselbe Helfer bedient jetzt auch die
   *  Lieferadresse (`deliveryAddress*`) — `field` ist deshalb ein String statt eines engeren
   *  Union-Typs, der Aufrufer waehlt das Praefix. */
  async function patchEventAddress(field: string, value: string) {
    if (!eventId) return;
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { [field]: value.trim() || null });
      onEventChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Eckdaten des Vorgangs"
      subtitle="Datum, Gästezahl, Uhrzeit und Adresse dieses Auftrags – bei mehrtägigen Vorgängen zusätzlich die Tagesplanung."
      // Bughunt 01.09.2026 (Antoine): jedes Feld hier speichert einzeln per onBlur/onChange sofort
      // (kein Sammel-Speichern-Knopf) — die "Ungespeicherte Änderungen"-Warnung war ein Fehlalarm.
      autosave
    >
      <div className="space-y-5" data-testid="builder-eckdaten-drawer">
        <div className="grid gap-3.5 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Datum des Vorgangs
            </span>
            <DateInput
              data-testid="builder-eckdaten-datum"
              // Unkontrolliert + `key` am Wert: nach der Server-Antwort steht der frische Wert
              // im Feld, waehrend des Tippens greift nichts hinein (Muster wie „Angebots-
              // Gültigkeit" in NachschlagenTabs.tsx).
              key={`ed-${eventDate ?? ""}`}
              defaultValue={eventDate ?? ""}
              onCommit={(v) => void patchEventDate(v)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Gäste (Vorgang)
            </span>
            <Input
              type="number"
              min={0}
              data-testid="builder-eckdaten-gaeste-event"
              key={`eg-${eventGuestCount ?? ""}`}
              defaultValue={eventGuestCount ?? ""}
              onBlur={(e) => {
                const n = Number(e.target.value);
                if (!Number.isInteger(n) || n < 0) return setError("Ungültige Gästezahl – bitte eine ganze Zahl angeben.");
                void patchEventGuestCount(n);
              }}
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Uhrzeit
          </span>
          <div
            className="flex items-center gap-1.5"
            data-testid="builder-eckdaten-uhrzeit"
            onBlur={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              const [von, bis] = Array.from(e.currentTarget.querySelectorAll("input")).map((el) => (el as HTMLInputElement).value);
              if (von) void patchEventTime(von, bis ?? "");
            }}
          >
            {/* Design-Komponenten-Migration (P1): `ZeitEingabe` bleibt unangetastet — sie ist
                bereits die richtige, spezialisierte Komponente (HH:mm-Eigenformat statt
                Browser-Zeit-Picker, s. ihre Definition in ui/components.tsx), keine rohe Stelle,
                die auf `Input` umgestellt werden muesste. Der className-Wert unten (Basisklasse
                plus max-w) ist nur ihr eigener, durchgereichter Style-Prop (Default in
                `ZeitEingabe` ohnehin dieselbe Basisklasse) — kein Kandidat fuer die Ratsche im
                eigentlichen Sinn. */}
            <ZeitEingabe name="eckdatenZeitVon" defaultValue={eventTimeFrom?.slice(0, 5) ?? ""} className="max-w-[100px]" />
            <span aria-hidden>–</span>
            <ZeitEingabe name="eckdatenZeitBis" defaultValue={eventTimeTo?.slice(0, 5) ?? ""} className="max-w-[100px]" />
          </div>
        </label>

        {/* Migration 350 (Auftragsadresse, docs/KONZEPT-AUFTRAGSADRESSE.md § 5): eigene, vom
            Kunden abweichende Liefer-/Veranstaltungsadresse. Gesetzt (alle drei Felder), hat sie
            Vorrang vor der Kundenadresse fuer Wetter/Ort UND Rechnung; leer, gilt weiterhin die
            Kundenadresse. Kein stillschweigendes Ersetzen - die Kundenadresse bleibt daneben
            lesbar sichtbar. WICHTIG: dieser Abschnitt bleibt IMMER sichtbar im Drawer (nicht an
            editable && option gekoppelt) - die Adresse soll laut Konzept "immer verfuegbar" sein,
            auch an einem bereits gebundenen Auftrag (CONTRACT_LOCKED_FIELDS schliesst sie bewusst
            aus, s. routes/events.ts). */}
        <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
          <div>
            <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Adresse dieses Auftrags
            </span>
            <p className="mt-1 font-body-sm text-[11.5px] text-secondary-fixed-dim">
              Nur ausfüllen, wenn dieser Auftrag an einer anderen Adresse als der Kunde
              stattfindet/beliefert wird. Leer = es gilt weiterhin die Kundenadresse (für Wetter,
              Ort und Rechnung).
            </p>
          </div>
          <AdresseFelder
            werte={{
              company: eventAddressCompany ?? null,
              name: eventAddressName ?? null,
              street: eventAddressStreet ?? null,
              addition: eventAddressAddition ?? null,
              zip: eventAddressZip ?? null,
              city: eventAddressCity ?? null,
              countryCode: eventAddressCountryCode ?? null,
            }}
            onFeldChange={(feld: AdresseFeld, wert: string) => void patchEventAddress(`eventAddress${feldSuffix(feld)}`, wert)}
            firmaName="betreiber"
            commitOn="blur"
            laender={LAENDER}
            testIdPrefix="builder-eckdaten-adresse"
          />
          {(customerAddressStreet || customerAddressZip || customerAddressCity) && (
            <p className="rounded-lg bg-surface-container-high px-3 py-2 font-body-sm text-[11.5px] text-on-surface-variant dark:bg-white/[0.08]">
              <span className="font-semibold">Kundenadresse (Standard):</span>{" "}
              {adresseEinzeilig({
                name: customerName,
                company: customerCompany,
                street: customerAddressStreet,
                addition: customerAddressAddition,
                zip: customerAddressZip,
                city: customerAddressCity,
                countryCode: customerAddressCountryCode,
              })}
            </p>
          )}
        </div>

        {/* WP3b (Migration 385, Auftrags-Dashboard „Phase C.1"): eigenstaendige Lieferadresse,
            GETRENNT von der Adresse dieses Auftrags oben ("Rechnungsadresse", eventAddress*).
            Keine zweite Wahrheit — derselbe Helfer (`patchEventAddress`, jetzt generisch), dasselbe
            Editier-Muster, IMMER sichtbar (nicht an editable && option gekoppelt), aus demselben
            Grund wie oben nicht in CONTRACT_LOCKED_FIELDS. Fliesst NIE in eine Rechnung ein — nur
            Vorrang vor der Adresse dieses Auftrags/Kundenadresse fuer Wetter/Ort. */}
        <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
          <div>
            <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Lieferadresse
            </span>
            <p className="mt-1 font-body-sm text-[11.5px] text-secondary-fixed-dim">
              Nur ausfüllen, wenn geliefert wird und die Lieferadresse von der Adresse dieses
              Auftrags/der Kundenadresse abweicht. Maßgeblich für Wetter/Ort, wenn gesetzt.
            </p>
          </div>
          <AdresseFelder
            werte={{
              company: deliveryAddressCompany ?? null,
              name: deliveryAddressName ?? null,
              street: deliveryAddressStreet ?? null,
              addition: deliveryAddressAddition ?? null,
              zip: deliveryAddressZip ?? null,
              city: deliveryAddressCity ?? null,
              countryCode: deliveryAddressCountryCode ?? null,
            }}
            onFeldChange={(feld: AdresseFeld, wert: string) => void patchEventAddress(`deliveryAddress${feldSuffix(feld)}`, wert)}
            firmaName="betreiber"
            commitOn="blur"
            laender={LAENDER}
            testIdPrefix="builder-eckdaten-lieferadresse"
          />
        </div>

        {/* Ohne geladene Variante gibt es weder eine Varianten-Gaestezahl noch eine
            Tagesplanung — dieselbe Bedingung, unter der frueher der Reiter „Variante
            bearbeiten" ueberhaupt erst erschien (`editable && option`). */}
        {editable && option && (
          <>
            {/* Antoine-Fehlerbericht 15.10.2026: KEINE eigene Gästezahl je Variante mehr — die
                Gästezahl des Vorgangs oben gilt für alle Varianten (Server zieht mit). */}
            {/* ---- Tagesplanung, wortgleich aus dem entfallenen „Variante bearbeiten"-Reiter ---- */}
            <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
              <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Tagesplanung
              </span>
              {days.length === 0 ? (
                <Button type="button" variant="ghost" className="!px-2 text-primary" onClick={onEnableDays}>
                  <Icon name="calendar_add_on" className="text-[18px]" /> Mehrtägig planen
                </Button>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {days.map((d) => (
                    <button
                      key={d.dayNo}
                      type="button"
                      onClick={() => onSelectDay(d.dayNo)}
                      className={`rounded-lg px-3 py-1.5 font-label-md text-label-md ${d.dayNo === activeDay ? "bg-secondary-container text-on-secondary-container dark:bg-surface-variant" : "bg-surface-container-high text-on-surface-variant dark:bg-white/[0.08]"}`}
                    >
                      {d.label ?? `Tag ${d.dayNo + 1}`} · {d.guestCount} G.
                    </button>
                  ))}
                  {/* Positionen ohne Tageszuordnung waren bisher in KEINEM Reiter zu sehen und
                      zaehlten trotzdem in die Summe (siehe OHNE_TAG in builder.tsx). */}
                  {zeigeOhneTagReiter && (
                    <button
                      type="button"
                      onClick={() => onSelectDay(OHNE_TAG)}
                      className={`rounded-lg px-3 py-1.5 font-label-md text-label-md ${activeDay === OHNE_TAG ? "bg-secondary-container text-on-secondary-container dark:bg-surface-variant" : "bg-surface-container-high text-on-surface-variant dark:bg-white/[0.08]"}`}
                    >
                      Ohne Tag · {ohneTagAnzahl}
                    </button>
                  )}
                  <Button type="button" variant="ghost" className="!px-2" onClick={onAddDay} title="Tag hinzufügen">
                    <Icon name="add" className="text-[18px]" />
                  </Button>
                </div>
              )}
              {days.length > 0 && activeDay != null && activeDay !== OHNE_TAG && (
                <div className="grid gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/30 sm:grid-cols-[1fr_120px_auto]">
                  <Input placeholder="Tages-Label" value={days.find((d) => d.dayNo === activeDay)?.label ?? ""} onChange={(e) => onPatchDay(activeDay, { label: e.target.value })} />
                  <Input type="number" value={days.find((d) => d.dayNo === activeDay)?.guestCount ?? 0} onChange={(e) => onPatchDay(activeDay, { guestCount: Math.max(0, Number(e.target.value) || 0) })} />
                  <Button type="button" variant="ghost" className="!px-2 text-error" onClick={() => onRequestDeleteDay(activeDay)} title="Tag entfernen">
                    <Icon name="delete" className="text-[18px]" />
                  </Button>
                </div>
              )}
              {/* Der Reiter braucht einen Satz, sonst liest man ihn als „gehoert nirgends hin"
                  statt als „gilt fuer die ganze Veranstaltung" — und die Gaestezahl, mit der
                  diese Zeilen gerechnet werden, steht nirgends sonst. */}
              {activeDay === OHNE_TAG && (
                <p className="rounded-lg border border-outline-variant/30 p-3 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30">
                  Diese Positionen gehören zu keinem einzelnen Tag. Sie zählen voll in den
                  Gesamtbetrag und rechnen bei „pro Person" mit der Gästezahl der Variante
                  ({option.guestCount}).
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
